import React, { createContext, useContext, useMemo } from 'react';
import { BrandingConfig } from '../types';
import { DEFAULT_BRANDING } from '../constants';

/**
 * Interface pour les valeurs de branding résolues (avec fallbacks appliqués)
 */
interface ResolvedBranding {
  name?: string;
  logo1: string;
  logo2?: string;
  primaryColor: string;
  secondaryColor: string;
  favicon: string;
  sponsorLogos?: { url: string; alt: string }[];
}

/**
 * Context pour le branding
 */
const BrandingContext = createContext<ResolvedBranding | null>(null);

interface BrandingProviderProps {
  branding?: BrandingConfig;
  children: React.ReactNode;
}

/**
 * Provider qui fournit les valeurs de branding avec fallbacks appliqués
 */
export function BrandingProvider({ branding, children }: BrandingProviderProps) {
  const resolvedBranding = useMemo<ResolvedBranding>(() => ({
    name: branding?.name,
    logo1: branding?.logo1 || DEFAULT_BRANDING.logo1,
    logo2: branding?.logo2,
    primaryColor: branding?.primaryColor || DEFAULT_BRANDING.primaryColor,
    secondaryColor: branding?.secondaryColor || DEFAULT_BRANDING.secondaryColor,
    favicon: branding?.favicon || DEFAULT_BRANDING.favicon,
    sponsorLogos: branding?.sponsorLogos
  }), [branding]);

  return (
    <BrandingContext.Provider value={resolvedBranding}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook pour accéder aux valeurs de branding
 * @throws Error si utilisé en dehors d'un BrandingProvider
 */
export function useBranding(): ResolvedBranding {
  const context = useContext(BrandingContext);

  if (!context) {
    // Fallback si pas de provider (pour compatibilité)
    return {
      logo1: DEFAULT_BRANDING.logo1,
      primaryColor: DEFAULT_BRANDING.primaryColor,
      secondaryColor: DEFAULT_BRANDING.secondaryColor,
      favicon: DEFAULT_BRANDING.favicon
    };
  }

  return context;
}
